import type { FC } from 'react';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { generatePath } from 'react-router-dom';
import type { SxProps, TabsProps, Theme } from '@mui/material';
import { Tab, Tabs } from '@mui/material';
import classNames from 'classnames';

import { FILTER_KEY } from 'src/constants/keySession';
import { PermissionGroup } from 'src/constants/modulePermission';
import { SECTION } from 'src/constants/section';
import type { ModuleType } from 'src/types/modulePermission';
import { getModulePath } from 'src/helper/helper';
import history from 'src/services/routing/history';
import { routePaths } from 'src/services/routing/routePaths';
import { useSection } from 'src/components/app/context/SectionContext';
import { useUserContext } from 'src/components/app/context/UserContext';
import useUserPermissions from 'src/components/app/context/UserContext/useUserPermissions';
import { useGlobalDashboards } from 'src/components/common/GlobalModules/useGlobalDashboards';

import { styles } from './index.styles';

export const TAB_NAVIGATION_TEST_ID = 'header-tab-navigation';

interface TabNavigationProps extends TabsProps {
  sx?: SxProps<Theme>;
}

const TabNavigation: FC<TabNavigationProps> = ({ className, sx = [] }) => {
  const { user } = useUserContext();
  const { moduleId, section } = useSection();
  const { t, i18n } = useTranslation('common');

  const {
    getPermissionsForGroup,
    activeUserPermissionsInCurrentSurvey,
    hasPermissionForModuleTypeInAnySurvey,
    hasMyFollowUpAccess,
  } = useUserPermissions();

  const hasAdminPermissions = useMemo(() => {
    const includeInactivePermissions = true;
    return (
      getPermissionsForGroup(PermissionGroup.ADMIN, includeInactivePermissions)
        .length > 0
    );
  }, [getPermissionsForGroup]);

  const tabsWithoutCurrentSurvey = [
    {
      moduleId: SECTION.SURVEY,
      moduleType: SECTION.SURVEY,
      moduleName: 'myResults',
      slug: undefined,
      hasPermission: true,
    },
    {
      moduleId: SECTION.MY_FOLLOW_UP,
      moduleType: SECTION.MY_FOLLOW_UP,
      moduleName: 'myFollowUp',
      slug: undefined,
      hasPermission: hasMyFollowUpAccess(),
    },
  ];
  const moduleIdsWithoutCurrentSurvey = tabsWithoutCurrentSurvey.map(
    ({ moduleId }) => moduleId,
  );

  const { data: globalDashboards } = useGlobalDashboards(!!user);

  /**
   * Handle active tab change.
   */
  const handleChange = useCallback(
    (moduleType: ModuleType, slug?: string | null) => {
      const route = getModulePath(moduleType);
      let params = undefined;
      if (route === routePaths.DASHBOARD_DETAIL) {
        if (!slug) {
          throw new Error('Dashboard module is missing a valid slug.');
        }

        params = { slug: slug };
      }
      const path = generatePath(route, params);

      // Clear filter settings from sessionStorage if we switch tabs away from userAdmin
      // find more information about why we need to handle filter settings like this in UserListPage.
      if (moduleId === SECTION.USER_ADMIN) {
        sessionStorage.removeItem(FILTER_KEY);
      }

      history.push(path);
    },
    [moduleId, history],
  );

  const surveyTabs = useMemo(() => {
    // Create tabs from permissions, but hide MY_FOLLOW_UP. Also do not display hidden tabs except for superusers.
    const permissionTabs = activeUserPermissionsInCurrentSurvey
      .filter(
        (permission) =>
          !moduleIdsWithoutCurrentSurvey.includes(
            permission.module.module_type,
          ) &&
          ((user && (user.is_superuser || user.isFrontendAdmin)) ||
            !permission.module.hide_tab),
      )
      .map((permission) => (
        <Tab
          key={permission.module.id}
          label={permission.module.name}
          onClick={() =>
            handleChange(permission.module.module_type, permission.module.slug)
          }
          sx={styles.tab}
          value={permission.module.id}
        />
      ));

    // Add admin tab if the user is admin
    if (hasAdminPermissions) {
      permissionTabs.push(
        <Tab
          key="admin-tab"
          label={t('userAdmin')}
          onClick={() => handleChange(SECTION.USER_ADMIN)}
          sx={styles.tab}
          value={SECTION.USER_ADMIN}
        />,
      );
    }

    return permissionTabs;
  }, [
    activeUserPermissionsInCurrentSurvey,
    handleChange,
    hasAdminPermissions,
    user,
    t,
  ]);

  const globalTabs = useMemo(
    () =>
      [
        ...tabsWithoutCurrentSurvey,
        ...(globalDashboards?.map((gD) => ({
          moduleId: gD.id,
          moduleName: gD.name,
          hasPermission: hasMyFollowUpAccess(),
          slug: gD.slug,
          moduleType: gD.module_type,
        })) ?? []),
      ].map(
        ({
          moduleId: tabModuleId,
          moduleName,
          slug,
          moduleType,
          hasPermission,
        }) =>
          hasPermission && (
            <Tab
              key={`tab-${tabModuleId}${slug ? `-${slug}` : ''}`}
              label={t(moduleName)}
              onClick={() => handleChange(moduleType as ModuleType, slug)}
              sx={styles.tab}
              value={tabModuleId}
            />
          ),
      ),
    [
      handleChange,
      hasPermissionForModuleTypeInAnySurvey,
      globalDashboards,
      i18n.language,
    ],
  );

  if (!moduleId || !user) return null;

  const isGlobalDashboard =
    section === SECTION.DASHBOARD &&
    globalDashboards?.some((gD) => gD.id === moduleId);

  const isSurveySelection = [SECTION.SURVEY, SECTION.MY_FOLLOW_UP].includes(
    section,
  );

  if (isSurveySelection || isGlobalDashboard) {
    return (
      <Tabs
        className={classNames('TabNavigation-root', className)}
        scrollButtons="auto"
        sx={[styles.tabs, ...(Array.isArray(sx) ? sx : [sx])]}
        value={
          section === SECTION.MY_FOLLOW_UP ? SECTION.MY_FOLLOW_UP : moduleId
        }
        variant="scrollable"
        data-test-id={TAB_NAVIGATION_TEST_ID}
      >
        {globalTabs}
      </Tabs>
    );
  }

  return (
    <Tabs
      className={classNames('TabNavigation-root', className)}
      scrollButtons="auto"
      sx={[styles.tabs, ...(Array.isArray(sx) ? sx : [sx])]}
      value={moduleId}
      variant="scrollable"
      data-test-id={TAB_NAVIGATION_TEST_ID}
      slotProps={{ scrollButtons: { sx: styles.scrollButton } }}
    >
      {surveyTabs}
    </Tabs>
  );
};

export default TabNavigation;
